import { useTRPC } from '@/utils/trpc';
import {
  AddSocialHistoryForm,
  type AddSocialHistoryFormValues,
} from '@liza/ui/components/records/AddSocialHistoryForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@liza/ui/components/ui/dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

type Props = {
  medplumPatientId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AddSocialHistoryDialog = ({ medplumPatientId, isOpen, onOpenChange }: Props): React.ReactNode => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync: createObservation, isPending: isCreating } = useMutation(
    trpc.patient.observation.create.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.observation.list.queryKey({ medplumPatientId, category: 'social-history' }),
        });
      },
    }),
  );

  const handleCreate = useCallback(
    async (values: AddSocialHistoryFormValues): Promise<void> => {
      try {
        await createObservation({
          resource: {
            resourceType: 'Observation',
            status: 'final',
            category: [
              {
                coding: [
                  { system: 'http://terminology.hl7.org/CodeSystem/observation-category', code: 'social-history' },
                ],
              },
            ],
            code: {
              coding: [values.topic],
              text: values.topic.display ?? values.topic.code,
            },
            subject: { reference: `Patient/${medplumPatientId}` },
            effectiveDateTime: new Date().toISOString(),
            valueString: values.value,
            note: values.notes ? [{ text: values.notes }] : undefined,
          },
        });
        toast.success(t('socialHistoryAdded'));
        onOpenChange(false);
      } catch (error) {
        toast.error(`${t('addSocialHistoryFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createObservation, medplumPatientId, onOpenChange, t],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('addSocialHistory')}</DialogTitle>
        </DialogHeader>
        <AddSocialHistoryForm isCreating={isCreating} onClose={() => onOpenChange(false)} onCreate={handleCreate} />
      </DialogContent>
    </Dialog>
  );
};
